import { useEffect, useState } from "react";
import { SignUp } from "@clerk/clerk-react";
import { AuthLayout, ClerkNotConfiguredPlaceholder, clerkAppearance } from "../components/AuthLayout";

const LEXSITE_URL = (import.meta.env.VITE_LEXSITE_URL ?? "http://localhost:5176").replace(/\/$/, "");
const EAP_URL = `${LEXSITE_URL}/early-access`;
const clerkPk = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;

const REDIRECT_SECONDS = 10;

/**
 * Detect a Clerk invitation ticket. With the invitation `redirectUrl`
 * correctly set to this `/sign-up` page (lexserver config —
 * INVITATION_REDIRECT_URL), Clerk's FAPI processes the ticket and
 * redirects here with a clean top-level `?__clerk_ticket=…` QUERY param,
 * which `<SignUp/>` auto-detects. The hash branch is a defensive fallback
 * for the older/misconfigured shape (ticket nested in an encoded
 * redirect_url hash, `/sign-up#/?…&__clerk_ticket=…`) — kept so a stale
 * env value degrades gracefully rather than silently bouncing invitees
 * to EAP.
 */
function hasInvitationTicket(): boolean {
  if (typeof window === "undefined") return false;
  // Primary: top-level query param (correct redirectUrl=/sign-up flow).
  const inSearch = new URLSearchParams(window.location.search).has("__clerk_ticket");
  // Fallback: hash-nested shape from a misconfigured redirectUrl. Strip
  // the leading `#`/`#/` and scan the remainder for the substring (it may
  // be a nested, percent-encoded value inside redirect_url, so a plain
  // URLSearchParams key check isn't enough — substring is the safe net).
  const rawHash = window.location.hash.replace(/^#\/?/, "");
  const inHash =
    new URLSearchParams(rawHash).has("__clerk_ticket") ||
    rawHash.includes("__clerk_ticket");
  return inSearch || inHash;
}

export function SignUpPage() {
  // INVITATION CARVE-OUT (invitation-flow plan Phase 7, gap surfaced by
  // invitation-flow.spec.ts 2026-05-15): Lexiun is in private preview, so
  // OPEN sign-up is invitation-only via the EAP form. But an invited user
  // following their Clerk invitation link arrives here WITH a
  // `__clerk_ticket` — that ticket IS the funnel (plan constraint #4:
  // "open Clerk sign-up would bypass the invitation funnel"). The ticket
  // is the sanctioned exception: render Clerk's <SignUp> widget so the
  // invited user can set their password and accept. Everyone WITHOUT a
  // ticket still hits the private-preview EAP redirect, exactly as before.
  const isInvitation = hasInvitationTicket();

  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_SECONDS);

  useEffect(() => {
    if (isInvitation) return; // invited users are NOT redirected to EAP
    const interval = setInterval(() => {
      setSecondsLeft((s) => Math.max(0, s - 1));
    }, 1000);
    const timeout = setTimeout(() => {
      window.location.replace(EAP_URL);
    }, REDIRECT_SECONDS * 1000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isInvitation]);

  // ── Invitation accept surface ──────────────────────────────────────
  if (isInvitation) {
    return (
      <AuthLayout eapText="Already have an account? Sign in →">
        {clerkPk ? (
          // forceRedirectUrl: the invitation `redirectUrl` is THIS page
          // (/sign-up), so Clerk has no post-accept destination of its
          // own — send the freshly-accepted user to /home explicitly.
          // The SPA then POSTs to /api/auth/exchange → first-touch
          // reconciliation binds clerkUserId. (Mirrors the implicit
          // "/" → Navigate /home that normal sign-in relies on, but
          // deterministic for the invitation path.)
          <SignUp
            routing="path"
            path="/sign-up"
            appearance={clerkAppearance}
            forceRedirectUrl="/home"
          />
        ) : (
          <ClerkNotConfiguredPlaceholder mode="sign-up" />
        )}
      </AuthLayout>
    );
  }

  // ── Private-preview placeholder (no ticket — open sign-up funnels to EAP) ──
  return (
    <AuthLayout eapText="Don't have an invitation? Apply for early access →">
      <div
        role="status"
        aria-label="Lexiun is in private preview — auto-redirecting to early access"
        style={{
          background: "#111520",
          border: "1px solid rgba(212,160,82,0.2)",
          borderRadius: 4,
          padding: "36px 40px",
          maxWidth: 460,
          textAlign: "center",
          color: "rgba(242,238,233,0.85)",
          fontFamily: "'DM Sans', system-ui, sans-serif",
          fontSize: "0.9375rem",
          lineHeight: 1.55,
        }}
      >
        <div
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: "1.5rem",
            color: "#F2EEE9",
            marginBottom: 14,
          }}
        >
          Lexiun is in private preview
        </div>
        <div style={{ color: "rgba(242,238,233,0.7)", fontSize: "0.875rem", marginBottom: 28 }}>
          Sign-up is invitation-only during private preview. Please apply for
          the early access program — we review every application personally.
        </div>
        <a
          href={EAP_URL}
          style={{
            display: "inline-block",
            background: "#D4A052",
            color: "#0B0D11",
            fontSize: "0.9375rem",
            fontWeight: 600,
            textDecoration: "none",
            letterSpacing: "0.02em",
            fontFamily: "'DM Sans', system-ui, sans-serif",
            padding: "14px 36px",
            borderRadius: 4,
            boxShadow: "0 0 24px rgba(212,160,82,0.18)",
            transition: "background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease",
          }}
          onMouseEnter={(e) => {
            const s = (e.currentTarget as HTMLAnchorElement).style;
            s.background = "#E0B468";
            s.transform = "translateY(-1px)";
            s.boxShadow = "0 0 32px rgba(212,160,82,0.3)";
          }}
          onMouseLeave={(e) => {
            const s = (e.currentTarget as HTMLAnchorElement).style;
            s.background = "#D4A052";
            s.transform = "translateY(0)";
            s.boxShadow = "0 0 24px rgba(212,160,82,0.18)";
          }}
        >
          Apply for Early Access →
        </a>
        <div
          aria-live="polite"
          style={{
            marginTop: 24,
            color: "rgba(242,238,233,0.45)",
            fontSize: "0.75rem",
            letterSpacing: "0.02em",
          }}
        >
          {secondsLeft > 0
            ? `Auto-redirecting in ${secondsLeft}s…`
            : "Redirecting…"}
        </div>
      </div>
    </AuthLayout>
  );
}
